import { useState } from "react";
import { MapPin, Plus, Route as RouteIcon, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { BAKERY_CRAWL_ROUTE, bakeryCrawlUrl } from "@/data/bakeryCrawl";

// The interactive bakery crawl card — shared by /bakeries (route 1 section)
// and /bakery-crawl (both routes). Stops can be dropped and re-added; the
// Google Maps walking link always follows the remaining stops.
export const BakeryCrawlCard = ({
  route = BAKERY_CRAWL_ROUTE,
  kicker = "ONE WALK · EIGHT BAKERIES",
  heading = (
    <>
      The bakery crawl,{" "}
      <span className="font-editorial italic font-600 text-[#DF3226]">ready-made</span>
    </>
  ),
  copy = "Good bakeries, one address per brand. About 6 km, Østerbro to the inner city — make a walking day of it, or bike it.",
  idPrefix = "bakery-route",
  linkToGuide = false,
}) => {
  const [skipped, setSkipped] = useState([]);
  const stops = route.filter((s) => !skipped.includes(s.name));

  return (
    <Reveal y={30}>
      <div
        data-testid={idPrefix}
        className="bg-white border border-zinc-200 shadow-brut rounded-2xl p-8 md:p-12"
      >
        <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">
          {kicker}
        </p>
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <div className="flex-1">
            <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-5xl">
              {heading}
            </h2>
            <p className="font-body text-sm md:text-base text-black/60 mt-3 max-w-lg">
              {copy}
            </p>
            <p className="font-body text-xs text-black/40 mt-2">
              Tap ✕ on any stop to drop it — the Maps route updates.
              {linkToGuide && (
                <>
                  {" "}
                  <Link
                    to="/bakery-crawl"
                    data-testid="bakery-crawl-guide-link"
                    className="inline-flex items-center gap-0.5 font-800 text-[#DF3226] underline underline-offset-2 hover:text-black transition-colors"
                  >
                    The full crawl guide
                  </Link>
                </>
              )}
            </p>
            <ol className="flex flex-wrap gap-2 mt-5">
              {stops.map((s, i) => (
                <li
                  key={s.name}
                  data-testid={`${idPrefix}-stop-${i}`}
                  className="inline-flex items-center gap-1.5 font-body font-700 text-xs bg-[#F9F8F6] border border-zinc-200 rounded-full pl-3 pr-2 py-1.5"
                >
                  <span className="font-display font-900 text-[#DF3226]">{i + 1}</span>
                  {s.img && (
                    <img src={s.img} alt="" loading="lazy" className="w-6 h-6 rounded-full object-cover" />
                  )}
                  {s.name}
                  <span className="text-black/40">· {s.street}</span>
                  <button
                    type="button"
                    data-testid={`${idPrefix}-remove-${i}`}
                    aria-label={`Remove ${s.name} from the route`}
                    onClick={() => setSkipped((p) => [...p, s.name])}
                    className="ml-0.5 rounded-full p-0.5 text-black/30 hover:text-[#DF3226] hover:bg-black/5 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ol>
            {skipped.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="font-body text-xs text-black/40">Off the route:</span>
                {route.filter((s) => skipped.includes(s.name)).map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    data-testid={`${idPrefix}-readd-${s.name.replace(/\s+/g, "-")}`}
                    onClick={() => setSkipped((p) => p.filter((n) => n !== s.name))}
                    className="inline-flex items-center gap-1 font-body font-700 text-xs text-black/40 border border-dashed border-zinc-300 rounded-full px-3 py-1.5 hover:text-black hover:border-zinc-400 transition-colors"
                  >
                    <Plus size={11} /> {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {stops.length >= 2 && (
            <a
              href={bakeryCrawlUrl(stops)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${idPrefix}-open-btn`}
              className="shrink-0 inline-flex items-center gap-2 bg-[#DF3226] text-white font-body font-800 text-sm px-5 py-3 rounded-full hover:bg-white transition-colors"
            >
              <MapPin size={16} /> Open the whole route in Google Maps
            </a>
          )}
        </div>
      </div>
    </Reveal>
  );
};
